import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { getBackfillActivity } from '../../api/contentCompletion';
import {
  BackfillActivity,
  BackfillActivityListResponse,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

interface BackfillActivityPanelProps {
  refreshToken?: number;
  companies: CompanyContentHealth[];
  onSelectCompany: (company: CompanyContentHealth) => void;
}

const BackfillActivityPanel: React.FC<BackfillActivityPanelProps> = ({
  refreshToken = 0,
  companies,
  onSelectCompany,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [data, setData] = useState<BackfillActivityListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBackfillActivity(7, 100);
      setData(response);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to load backfill activity';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivity();
  }, [loadActivity, refreshToken]);

  const summary = data?.summary;
  const activities = data?.activities ?? [];
  const companyById = new Map(companies.map((c) => [c.company_id, c]));

  const openCompany = (activity: BackfillActivity) => {
    const match = companyById.get(activity.company_id);
    if (match) {
      onSelectCompany(match);
      return;
    }
    onSelectCompany({
      company_id: activity.company_id,
      company_name: activity.company_name,
      press_release_url: activity.source_url,
      has_feed_configured: true,
      pr_total_count: 0,
      pr_recent_count: 0,
      quartr_total_count: 0,
      quartr_recent_count: 0,
      has_quartr_feed: false,
      status: 'no_feed',
      quartr_status: 'no_feed',
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          mb: expanded ? 1.5 : 0,
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <HistoryIcon color="secondary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          Backfill activity (last {data?.retention_days ?? 7} days)
        </Typography>
        {summary && (
          <>
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              label={`${summary.companies_filled} compan${
                summary.companies_filled === 1 ? 'y' : 'ies'
              }`}
            />
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              label={`${summary.articles_created} article${
                summary.articles_created === 1 ? '' : 's'
              }`}
            />
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={(e) => {
          e.stopPropagation();
          setExpanded((prev) => !prev);
        }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} />}
        {!loading && !error && summary && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {summary.events === 0
                ? 'No backfills recorded in this environment yet.'
                : `Filled ${summary.companies_filled} compan${
                    summary.companies_filled === 1 ? 'y' : 'ies'
                  } · ${summary.articles_created} article${
                    summary.articles_created === 1 ? '' : 's'
                  } created${
                    summary.articles_skipped > 0
                      ? ` · ${summary.articles_skipped} skipped`
                      : ''
                  }.`}
            </Typography>

            {summary.by_user.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {summary.by_user.map((user) => (
                  <Chip
                    key={user.performed_by}
                    size="small"
                    label={`${user.performed_by}: ${user.companies_filled} co · ${user.articles_created} arts`}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            {activities.length > 0 && (
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Who</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell align="right">Created</TableCell>
                      <TableCell align="right">Skipped</TableCell>
                      <TableCell>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((activity) => (
                      <TableRow
                        key={activity.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openCompany(activity)}
                      >
                        <TableCell>{formatDate(activity.performed_at)}</TableCell>
                        <TableCell>{activity.performed_by}</TableCell>
                        <TableCell>{activity.company_name}</TableCell>
                        <TableCell align="right">{activity.articles_created}</TableCell>
                        <TableCell align="right">{activity.articles_skipped}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={activity.source_url}
                        >
                          {activity.source_url}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default BackfillActivityPanel;
